import { Component, OnInit, ViewChild, ElementRef, inject, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { SalesFunnelDataService } from '../shared/sales-funnel-data.service';
import { ISalesFunnelLayoutChild } from '../shared/sales-funnel-child.module';

@Component({
  selector: 'app-customer-offer',
  standalone: true,
  imports: [],
  templateUrl: './customer-offer-component.html',
  styleUrls: ['./customer-offer-component.scss']
})
export class CustomerOfferComponent implements OnInit, AfterViewInit, ISalesFunnelLayoutChild {
  private router = inject(Router);
  private dataService = inject(SalesFunnelDataService);

  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  hasSignature = false;
  salesFunnelIndex = 3;

  contractText = `
TELEAUORA BROADBAND SERVICE AGREEMENT

Customer: ${this.getCustomerName()}
Address: ${this.getCustomerAddress()}
Email: ${this.getCustomerEmail()}
Phone: ${this.getCustomerPhone()}

SERVICE DETAILS:
Package: ${this.getPackageName()}
Speed: ${this.getPackageSpeed()}
Monthly Fee: ${this.getPackagePrice()}

TERMS:
- 24-month minimum contract period
- 30-day notice period for cancellation
- Installation within 2-4 weeks
- Equipment remains property of Teleauora
- Fair usage policy applies

By signing below, the customer agrees to the terms and conditions.`;

  private isDrawing = false;
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;

  ngOnInit(): void {
    this.initializeCanvas();
  }

  ngAfterViewInit(): void {
    this.setupCanvas();
  }

  private initializeCanvas(): void {
    setTimeout(() => {
      if (this.canvasRef) {
        this.setupCanvas();
      }
    }, 100);
  }

  private setupCanvas(): void {
    this.canvas = this.canvasRef.nativeElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Set canvas size
    const rect = this.canvas.parentElement!.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = 200;

    // Set background to white
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.font = '14px monospace';
    this.ctx.textAlign = 'center';
    this.ctx.fillStyle = '#606060';
    this.ctx.fillText('Draw signature here', this.canvas.width / 2, this.canvas.height / 2);

    // Add event listeners
    this.canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    this.canvas.addEventListener('mousemove', (e) => this.draw(e));
    this.canvas.addEventListener('mouseup', () => this.stopDrawing());
    this.canvas.addEventListener('mouseout', () => this.stopDrawing());

    // Touch support
    this.canvas.addEventListener('touchstart', (e) => this.startDrawing(e));
    this.canvas.addEventListener('touchmove', (e) => this.draw(e));
    this.canvas.addEventListener('touchend', () => this.stopDrawing());
  }

  private startDrawing(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    if (!this.hasSignature) {
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    this.isDrawing = true;
    this.hasSignature = true;

    const rect = this.canvas.getBoundingClientRect();
    const pos = this.getMousePos(event, rect);

    this.ctx.beginPath();
    this.ctx.moveTo(pos.x, pos.y);
  }

  private draw(event: MouseEvent | TouchEvent): void {
    if (!this.isDrawing) return;

    event.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const pos = this.getMousePos(event, rect);

    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = '#000000';
    this.ctx.lineTo(pos.x, pos.y);
    this.ctx.stroke();
  }

  private stopDrawing(): void {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  private getMousePos(event: MouseEvent | TouchEvent, rect: DOMRect): { x: number; y: number } {
    if (window.TouchEvent && event instanceof TouchEvent) {
      const touch = event.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    } else if (event instanceof MouseEvent) {
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    } else {
      return {
        x: 0,
        y: 0
      }
    }
  }

  clearSignature(): void {
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#000000';
    this.ctx.fillText('Draw signature here', this.canvas.width / 2, this.canvas.height / 2);

    this.hasSignature = false;
  }

  goBack(): void {
    this.router.navigate(['/customer-details']);
  }

  submitOrder(): void {
    if (this.hasSignature && this.canvasRef) {
      const signatureData = this.canvas.toDataURL('image/png');
      this.dataService.updateSignature(signatureData);
      this.router.navigate(['/order-confirmation']);
    }
  }

  private getCustomerName(): string {
    const data = this.dataService.getData();
    return `${data.customerDetails?.firstName || ''} ${data.customerDetails?.lastName || ''}`.trim();
  }

  private getCustomerAddress(): string {
    return [
      this.dataService.getData().lead?.address,
      'Upper Heyford',
      this.dataService.getData().lead?.postcode
    ].filter(e => e).join(', ')
  }

  private getCustomerEmail(): string {
    return this.dataService.getData().customerDetails?.email || '';
  }

  private getCustomerPhone(): string {
    return this.dataService.getData().customerDetails?.phone || '';
  }

  private getPackageName(): string {
    return this.dataService.getData().selectedPackage?.name || 'Package';
  }

  private getPackageSpeed(): string {
    const speed = this.dataService.getData().selectedPackage?.speed || 0;
    return speed < 0 ? 'Unlimited speed' : `${speed} Mbps`;
  }

  private getPackagePrice(): string {
    return this.dataService.getData().selectedPackage?.price || '';
  }
}
